import type { KeyboardEvent, ReactNode, Ref, RefObject } from 'react';
import { fieldPillClass } from '../field_trigger.js';

/*
 * O GATILHO do campo de lugar (`PlaceField`) e o input que mora nele: a casca
 * (`TriggerShell`, um `div` porque aberto hospeda o `<input>`), o miolo
 * (`TriggerContent`: glifo + input/valor/vazio) e o `PlaceSearchInput`, que
 * encaminha setas e Enter pro cmdk do painel. Saíram de `place_field.tsx` pelo
 * teto de linhas dele; a folha (`place_sheet.tsx`) e o popover usam os mesmos.
 */

/* Só as teclas que são DA LISTA. Home/End/PageUp ficam com o caret do input —
   roubá-las pro cmdk quebraria a edição do texto digitado. */
const LIST_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter']);

/** O input que mora NO GATILHO enquanto o popover está aberto. `panelRef` aponta
 *  pro wrapper do painel — é lá que mora o `[cmdk-root]` alvo das teclas. */
export function PlaceSearchInput({
  term,
  onTerm,
  placeholder,
  panelRef,
  label,
  onEscape,
  autoFocus = false,
  inputRef,
}: {
  readonly term: string;
  readonly onTerm: (term: string) => void;
  /** O valor atual do campo (ou o vazio dele) — digitar substitui, então o valor
   *  vira dica. */
  readonly placeholder?: string;
  readonly panelRef: RefObject<HTMLElement | null>;
  /** Nome acessível do campo (`Origem`, `Destino`, `Onde`). */
  readonly label: string;
  /** O Escape fecha SÓ este campo e MORRE aqui (stopPropagation): um host em
   *  camadas (painel de trechos, overlay) escuta Escape no `document`, e sem o
   *  corte um Esc dirigido à lista derrubaria a camada de trás junto. */
  readonly onEscape?: () => void;
  /** Se o input nasce com o cursor. Verdadeiro quando a abertura veio de um
   *  GESTO no campo (clique, Enter, `defaultOpen` do host); falso quando o
   *  host controlado abriu o popover por conta própria — o auto-avanço da barra
   *  de hotel não pode roubar o cursor de quem digita no composer da conversa.
   *  Opcional para quem consome o input solto; o padrão é não focar. */
  readonly autoFocus?: boolean;
  /** O input em si — pra quem o foca depois da montagem (a folha, ao terminar
   *  de entrar). */
  readonly inputRef?: Ref<HTMLInputElement>;
}): ReactNode {
  const forward = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && onEscape) {
      e.preventDefault();
      e.stopPropagation();
      onEscape();
      return;
    }
    if (!LIST_KEYS.has(e.key)) return;
    const root = panelRef.current?.querySelector('[cmdk-root]');
    if (!root) return;
    e.preventDefault();
    root.dispatchEvent(new KeyboardEvent('keydown', { key: e.key, bubbles: true }));
  };
  return (
    <input
      ref={inputRef}
      /* O input nasce quando o popover abre — `autoFocus` é o que tira o foco do
         gatilho e põe a pessoa digitando sem segundo clique. O popup recebe
         `initialFocus={false}` pra não disputar. */
      autoFocus={autoFocus}
      value={term}
      onChange={(e) => {
        onTerm(e.target.value);
      }}
      onKeyDown={forward}
      placeholder={placeholder}
      aria-label={label}
      /* A receita de vazio dos campos: placeholder em sand-500 e peso regular —
         o texto digitado volta pro `text-ui` cheio. */
      className="text-ui w-full min-w-0 bg-transparent outline-none placeholder:font-normal placeholder:text-(--sand-500)"
    />
  );
}

/** O que a pílula mostra preenchida: código em peso médio + resto em muted
 *  (`GRU São Paulo`) — a R-voo-7 no formulário: o código pega o erro de
 *  aeroporto, a palavra pega o erro de cidade. Sem código, o título puro. */
export type PlaceFieldValue = { code?: string; title: string };

/** A pele da pílula, no vocabulário do `Button` do DS: `outline` desenha o
 *  campo pela borda (o campo solto na página, as praças do aéreo); `ghost` é o
 *  campo DENTRO de uma cápsula que já tem borda — a barra compacta do hotel,
 *  onde os vizinhos Quem/Quando também são `ghost`. */
export type PlaceFieldVariant = 'outline' | 'ghost';

/** A pílula do campo — a `FieldTrigger` compartilhada das três modais, na
 *  forma de `div` (ver `TriggerShell`), com o anel do popover aberto por cima. */
function triggerClass(variant: PlaceFieldVariant): string {
  return `place-field-trigger ${fieldPillClass({ variant })}`;
}

/** A casca do gatilho, pro `render` do `PopoverTrigger` (o Base UI espalha as
 *  props dele por cima). Ver a nota no JSX do `PlaceField` sobre `<div>` em vez
 *  de `<button>` e o papel condicional. */
export function TriggerShell({
  open,
  disabled,
  label,
  variant,
  ...rest
}: {
  readonly open: boolean;
  readonly disabled: boolean;
  readonly label: string;
  readonly variant: PlaceFieldVariant;
} & Record<string, unknown>) {
  return (
    <div
      {...rest}
      role={open ? undefined : 'button'}
      tabIndex={disabled || open ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={open ? undefined : label}
      data-place-field-trigger=""
      /* Fechado o gatilho é botão e anuncia o clique (`cursor-pointer`, como o
         `Button` do DS que veste os campos vizinhos); aberto ele hospeda o
         input, e o cursor de texto é do próprio input. */
      className={`${triggerClass(variant)}${open ? '' : ' cursor-pointer'}${
        disabled ? ' pointer-events-none opacity-50' : ''
      }`}
    />
  );
}

/** O miolo do gatilho: glifo + o estado do campo (input da busca, valor ou
 *  vazio) — o `ConteudoDaPraca` do desenho, sem o esqueleto de geolocalização
 *  (nenhum host de hoje geolocaliza; entra quando um pedir). */
export function TriggerContent({
  open,
  value,
  emptyText,
  icon,
  label,
  term,
  onTerm,
  panelRef,
  onEscape,
  focusOnOpen,
}: {
  readonly open: boolean;
  readonly value?: PlaceFieldValue;
  readonly emptyText: string;
  readonly icon?: ReactNode;
  readonly label: string;
  readonly term: string;
  readonly onTerm: (term: string) => void;
  readonly panelRef: RefObject<HTMLDivElement | null>;
  readonly onEscape: () => void;
  readonly focusOnOpen: boolean;
}): ReactNode {
  return (
    <>
      {/* Tom do glifo = tom do placeholder (sand-500), vazio OU preenchido: o
          glifo é rótulo do campo, não valor — quem carrega o contraste quando o
          campo enche é o texto da praça. */}
      {icon && (
        <span className="text-(--sand-500) flex shrink-0 items-center" aria-hidden="true">
          {icon}
        </span>
      )}
      {open ? (
        <PlaceSearchInput
          term={term}
          onTerm={onTerm}
          panelRef={panelRef}
          label={label}
          onEscape={onEscape}
          autoFocus={focusOnOpen}
          placeholder={value ? [value.code, value.title].filter(Boolean).join(' ') : emptyText}
        />
      ) : value ? (
        <span className="text-ui min-w-0 truncate">
          {value.code ? <span className="font-medium">{value.code}</span> : null}
          {value.code ? ' ' : ''}
          <span className={value.code ? 'text-muted-foreground' : ''}>{value.title}</span>
        </span>
      ) : (
        <span className="text-ui text-(--sand-500) min-w-0 truncate font-normal">{emptyText}</span>
      )}
    </>
  );
}
